import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface EditWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspace: { id: string; name: string; description: string } | null;
  onSave?: (
    workspaceId: string,
    body: { name: string; description?: string },
  ) => Promise<void>;
}

export function EditWorkspaceDialog({
  open,
  onOpenChange,
  workspace,
  onSave,
}: EditWorkspaceDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && workspace) {
      setName(workspace.name);
      setDescription(workspace.description);
      setError(null);
    }
  }, [open, workspace]);

  async function handleSave() {
    if (!name.trim() || !workspace || !onSave) return;

    setSaving(true);
    setError(null);
    try {
      await onSave(workspace.id, {
        name: name.trim(),
        description: description.trim() || undefined,
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update workspace");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-glass-border bg-[#111b35] text-white sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Edit Workspace</DialogTitle>
          <DialogDescription className={`text-slate-400 ${pageDescriptionMobileHiddenClassName}`}>
            Update the workspace name and description.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="edit-workspace-name" className="text-slate-400">
              Name
            </Label>
            <Input
              id="edit-workspace-name"
              placeholder="e.g. Legal Research"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-workspace-desc" className="text-slate-400">
              Description (optional)
            </Label>
            <Input
              id="edit-workspace-desc"
              placeholder="What is this workspace for?"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          {error ? <p className="text-sm text-red-400">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="default" onClick={() => void handleSave()} disabled={saving || !name.trim()} className="gap-2">
            <Save className="h-4 w-4" />
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
